import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export function Tabs({
  items,
  value,
  onChange,
}: {
  items: readonly { id: string; label: string; content: ReactNode }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="reactive-tabs">
      <div className="reactive-tab-list" role="tablist">
        {items.map((item) => (
          <button
            key={item.id}
            role="tab"
            aria-selected={value === item.id}
            className={value === item.id ? 'active' : ''}
            onClick={() => onChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          hidden={value !== item.id}
          className="reactive-tab-panel"
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}

export function Accordion({
  items,
}: {
  items: readonly { title: string; content: ReactNode }[];
}) {
  const [open, setOpen] = useState<number>(0);

  return (
    <div className="accordion">
      {items.map((item, index) => {
        const expanded = open === index;
        return (
          <article className={expanded ? 'accordion-item open' : 'accordion-item'} key={item.title}>
            <button
              className="accordion-trigger"
              aria-expanded={expanded}
              onClick={() => setOpen(expanded ? -1 : index)}
            >
              <span>{item.title}</span>
              <i>⌄</i>
            </button>
            <div className="accordion-content" aria-hidden={!expanded}>
              <div>{item.content}</div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export function Dropdown({
  label,
  items,
  onSelect,
}: {
  label: string;
  items: readonly { id: string; label: string; detail?: string }[];
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', handlePointer);
    return () => window.removeEventListener('pointerdown', handlePointer);
  }, []);

  return (
    <div className="dropdown" ref={root}>
      <button className="btn secondary" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        {label} <span aria-hidden="true">⌄</span>
      </button>
      {open ? (
        <div className="dropdown-menu" role="menu">
          {items.map((item) => (
            <button
              key={item.id}
              role="menuitem"
              onClick={() => {
                onSelect(item.id);
                setOpen(false);
              }}
            >
              <strong>{item.label}</strong>
              {item.detail ? <span>{item.detail}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Tooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <span className="tooltip">
      <span className="tooltip-trigger" tabIndex={0}>{children}</span>
      <span className="tooltip-bubble" role="tooltip">{label}</span>
    </span>
  );
}

export function Drawer({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  return (
    <div className={open ? 'drawer-layer open' : 'drawer-layer'} aria-hidden={!open}>
      <button className="drawer-scrim" aria-label="Close drawer" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <header><h2>{title}</h2><button onClick={onClose} aria-label="Close drawer">×</button></header>
        <div className="drawer-content">{children}</div>
      </aside>
    </div>
  );
}

export function Stepper({
  steps,
  value,
  onChange,
}: {
  steps: readonly string[];
  value: number;
  onChange: (index: number) => void;
}) {
  return (
    <div className="stepper" aria-label="Progress steps">
      {steps.map((step, index) => (
        <button
          key={step}
          className={index === value ? 'active' : index < value ? 'complete' : ''}
          onClick={() => onChange(index)}
        >
          <i>{index < value ? '✓' : index + 1}</i>
          <span>{step}</span>
        </button>
      ))}
    </div>
  );
}

export function RangeField({
  label,
  value,
  min = 0,
  max = 100,
  onChange,
  suffix = '%',
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  suffix?: string;
}) {
  return (
    <label className="range-field">
      <span><b>{label}</b><em>{value}{suffix}</em></span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

export function Pagination({
  pages,
  value,
  onChange,
}: {
  pages: number;
  value: number;
  onChange: (page: number) => void;
}) {
  const list = useMemo(() => Array.from({ length: pages }, (_, index) => index + 1), [pages]);

  return (
    <nav className="pagination" aria-label="Pagination">
      <button disabled={value <= 1} onClick={() => onChange(value - 1)}>←</button>
      {list.map((page) => (
        <button
          key={page}
          className={page === value ? 'active' : ''}
          aria-current={page === value ? 'page' : undefined}
          onClick={() => onChange(page)}
        >
          {page}
        </button>
      ))}
      <button disabled={value >= pages} onClick={() => onChange(value + 1)}>→</button>
    </nav>
  );
}

export function DismissibleAlert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title: string;
  children: ReactNode;
}) {
  const [visible, setVisible] = useState(true);
  if (!visible) {
    return <button className="alert-restore" onClick={() => setVisible(true)}>Restore alert</button>;
  }

  return (
    <div className={'alert alert-' + tone} role="status">
      <i aria-hidden="true">●</i>
      <div><strong>{title}</strong><p>{children}</p></div>
      <button onClick={() => setVisible(false)} aria-label="Dismiss alert">×</button>
    </div>
  );
}

export function Skeleton({
  lines = 3,
}: {
  lines?: number;
}) {
  return (
    <div className="skeleton-card" aria-label="Loading content">
      <span className="skeleton-avatar" />
      <div>
        {Array.from({ length: lines }, (_, index) => (
          <span className="skeleton-line" key={index} />
        ))}
      </div>
    </div>
  );
}

export function MotionPreference() {
  const [reduced, setReduced] = useState(() => {
    const stored = localStorage.getItem('gpt-build-motion');
    if (stored) return stored === 'reduced';
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    document.documentElement.dataset.motion = reduced ? 'reduced' : 'full';
    localStorage.setItem('gpt-build-motion', reduced ? 'reduced' : 'full');
  }, [reduced]);

  return (
    <div className="motion-preference">
      <span><strong>Interface motion</strong><small>Animations respect this setting globally.</small></span>
      <button className={reduced ? '' : 'active'} onClick={() => setReduced((value) => !value)}>
        {reduced ? 'Reduced' : 'Full motion'}
      </button>
    </div>
  );
}
